import {
  Directive,
  ElementRef,
  HostListener,
  Renderer2,
  OnInit,
  AfterViewInit,
  Input,
  Optional,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import {
  formatInputDisplay,
  parseCurrencyInput,
  calculateCursorPosition,
  isValueWithinSafeRange,
  MAX_SAFE_CURRENCY,
} from '@gs-2.0/core';

/**
 * Currency input directive that formats numbers with thousand separators in real-time.
 *
 * Features:
 * - Formats numbers with thousand separators (e.g., 1,234.56)
 * - Restricts input to numeric characters and decimal point
 * - Maintains cursor position during formatting
 * - Stores unformatted numeric value in form control
 * - Forces exactly 2 decimal places on blur
 *
 * Usage:
 * ```html
 * <input [appCurrencyInput]="true" formControlName="amount" type="text" />
 * ```
 *
 * Note: Apply this directive to the native <input> element, not a wrapper component.
 */
@Directive({
  selector: 'input[appCurrencyInput]',
  standalone: true,
})
export class CurrencyInputDirective implements OnInit, AfterViewInit {
  @Input() appCurrencyInput: boolean = true;

  private initialValueSet = false;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
    @Optional() private ngControl: NgControl | null
  ) {}

  ngOnInit(): void {
    if (!this.appCurrencyInput) return;

    // Force input type to text for formatting support
    this.renderer.setAttribute(this.el.nativeElement, 'type', 'text');
  }

  ngAfterViewInit(): void {
    if (!this.appCurrencyInput) return;

    // Format initial value if present
    setTimeout(() => {
      if (this.ngControl && this.ngControl.control) {
        const currentValue = this.ngControl.control.value;
        if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
          const numericValue = typeof currentValue === 'number' ? currentValue : parseFloat(currentValue);
          if (!isNaN(numericValue)) {
            const formatted = numericValue.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
            this.renderer.setProperty(this.el.nativeElement, 'value', formatted);
            this.initialValueSet = true;
          }
        }
      }
    }, 0);
  }

  /**
   * Handle input event - format as user types
   */
  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    if (!this.appCurrencyInput) return;

    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const oldValue = input.value;

    // Format the value
    const formatted = formatInputDisplay(oldValue);

    // Parse to get numeric value for form control
    const numericValue = parseCurrencyInput(formatted);

    // Validate the value doesn't exceed safe range
    if (numericValue !== null && !isValueWithinSafeRange(numericValue)) {
      // Revert to previous valid value (stored in form control)
      if (this.ngControl && this.ngControl.control) {
        const previousValue = this.ngControl.control.value;
        if (previousValue !== null && previousValue !== undefined) {
          const previousFormatted = previousValue.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          this.renderer.setProperty(input, 'value', previousFormatted);
          input.setSelectionRange(cursorPosition - 1, cursorPosition - 1);
        }
      }
      return;
    }

    // Update display
    this.renderer.setProperty(input, 'value', formatted);

    // Calculate new cursor position
    const newPosition = calculateCursorPosition(
      oldValue,
      formatted,
      cursorPosition
    );
    input.setSelectionRange(newPosition, newPosition);

    // Update form control with numeric value
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(numericValue, { emitEvent: false });
      this.ngControl.control.markAsTouched();
    }
  }

  /**
   * Handle blur event - force 2 decimal places
   */
  @HostListener('blur')
  onBlur(): void {
    if (!this.appCurrencyInput) return;

    const input = this.el.nativeElement;
    let value = input.value;

    if (value.trim() === '') {
      // Empty input
      this.renderer.setProperty(input, 'value', '');

      // Update form control
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(null, { emitEvent: false });
      }
      return;
    }

    // Clean the value: remove commas and limit to 2 decimal places
    let cleaned = value.replace(/,/g, '');

    // If there are more than 2 decimal places, truncate to 2
    if (cleaned.includes('.')) {
      const parts = cleaned.split('.');
      if (parts[1] && parts[1].length > 2) {
        cleaned = `${parts[0]}.${parts[1].substring(0, 2)}`;
      }
    }

    // Parse the cleaned value
    const numericValue = parseFloat(cleaned);

    if (!isNaN(numericValue)) {
      // Validate the value doesn't exceed safe range
      if (!isValueWithinSafeRange(numericValue)) {
        // Clear invalid value and set form control to null
        this.renderer.setProperty(input, 'value', '');
        if (this.ngControl && this.ngControl.control) {
          this.ngControl.control.setValue(null, { emitEvent: false });
          this.ngControl.control.markAsTouched();
        }
        return;
      }

      // Format with exactly 2 decimal places
      const formatted = numericValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      this.renderer.setProperty(input, 'value', formatted);

      // Update form control
      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(numericValue, { emitEvent: false });
      }
    }
  }

  /**
   * Handle keydown event - restrict input to numeric characters
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.appCurrencyInput) return;
    // Allow: backspace, delete, tab, escape, enter
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Allow: Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
    if (
      (event.ctrlKey || event.metaKey) &&
      ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())
    ) {
      return;
    }

    // Allow: numbers (but check decimal limit)
    if (event.key >= '0' && event.key <= '9') {
      const input = event.target as HTMLInputElement;
      const cursorPosition = input.selectionStart || 0;
      const value = input.value;

      // Check if we're after a decimal point
      const decimalIndex = value.indexOf('.');
      if (decimalIndex !== -1) {
        // Get the decimal part (everything after the decimal point)
        const decimalPart = value.substring(decimalIndex + 1);
        // Count actual digits (remove commas if any, though there shouldn't be any after decimal)
        const afterDecimal = decimalPart.replace(/,/g, '');
        const cursorAfterDecimal = cursorPosition > decimalIndex;

        // If cursor is after decimal and there are already 2 digits
        if (cursorAfterDecimal && afterDecimal.length >= 2) {
          // Unless there's a selection (user is replacing text)
          const selectionLength = (input.selectionEnd || 0) - cursorPosition;
          if (selectionLength === 0) {
            event.preventDefault();

            // Calculate cursor position relative to decimal digits
            const cursorPositionInDecimals = cursorPosition - decimalIndex - 1;

            // Scenario 1: Cursor is right after decimal point (position 0)
            // Example: "1,234.|56" -> type "7" -> "1,234.76"
            if (cursorPositionInDecimals === 0) {
              const beforeDecimal = value.substring(0, decimalIndex + 1);
              const secondDecimalDigit = afterDecimal.charAt(1);
              const newValue = beforeDecimal + event.key + secondDecimalDigit;

              // Update the input value
              this.renderer.setProperty(input, 'value', newValue);

              // Set cursor to after the first decimal digit
              input.setSelectionRange(decimalIndex + 2, decimalIndex + 2);

              // Trigger input event to update form control
              const inputEvent = new Event('input', { bubbles: true });
              input.dispatchEvent(inputEvent);

              return;
            }

            // Scenario 2: Cursor is at the end (after both decimal digits)
            // Example: "1,234.56|" -> type "7" -> "1,234.56" (blocked, no change)
            if (cursorPositionInDecimals >= 2) {
              // Block input - do nothing
              return;
            }

            // Scenario 3: Cursor is between first and second decimal digit
            // Example: "1,234.5|6" -> type "7" -> "1,234.57"
            const beforeDecimal = value.substring(0, decimalIndex + 1);
            const firstDecimalDigit = afterDecimal.charAt(0);
            const newValue = beforeDecimal + firstDecimalDigit + event.key;

            // Update the input value
            this.renderer.setProperty(input, 'value', newValue);

            // Set cursor to end of decimal part
            input.setSelectionRange(decimalIndex + 3, decimalIndex + 3);

            // Trigger input event to update form control
            const inputEvent = new Event('input', { bubbles: true });
            input.dispatchEvent(inputEvent);

            return;
          }
        }
      }
      return;
    }

    // Allow: decimal point (only one)
    if (event.key === '.' || event.key === ',') {
      const input = event.target as HTMLInputElement;
      const cursorPosition = input.selectionStart || 0;
      const value = input.value;

      // If decimal already exists
      if (value.includes('.')) {
        const decimalIndex = value.indexOf('.');

        // If cursor is right before the decimal, move cursor to after it
        if (cursorPosition === decimalIndex) {
          event.preventDefault();
          input.setSelectionRange(decimalIndex + 1, decimalIndex + 1);
          return;
        }

        // Otherwise prevent multiple decimals
        event.preventDefault();
      }
      return;
    }

    // Block all other keys
    event.preventDefault();
  }

  /**
   * Handle paste event - clean and format pasted content
   */
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    if (!this.appCurrencyInput) return;

    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';

    // Clean and validate pasted text
    const cleaned = pastedText.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');

    // Allow only one decimal point and up to 2 decimal places
    let validValue = parts[0];
    if (parts.length > 1) {
      // Take first decimal point and limit to 2 decimal places
      validValue = `${parts[0]}.${parts[1].substring(0, 2)}`;
    }

    // Validate the pasted value doesn't exceed safe range
    if (validValue && !isValueWithinSafeRange(validValue)) {
      // Don't paste - value exceeds maximum
      return;
    }

    // Insert cleaned value
    const input = this.el.nativeElement;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentValue = input.value;

    // Build new value
    const newValue =
      currentValue.substring(0, start) +
      validValue +
      currentValue.substring(end);

    // Validate the final value doesn't exceed safe range
    const finalNumericValue = parseCurrencyInput(newValue);
    if (finalNumericValue !== null && !isValueWithinSafeRange(finalNumericValue)) {
      // Don't paste - final value would exceed maximum
      return;
    }

    // Set the new value and trigger formatting
    this.renderer.setProperty(input, 'value', newValue);

    // Trigger input event to format
    const inputEvent = new Event('input', { bubbles: true });
    input.dispatchEvent(inputEvent);

    // Set cursor position after pasted content
    const newPosition = start + validValue.length;
    setTimeout(() => {
      input.setSelectionRange(newPosition, newPosition);
    });
  }
}
